import {
  ClearShoppingCartRequestDto,
  EcommerceShoppingCartClient,
} from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Toast } from "shared-ui/src/components/Toast";
import { invalidateCart } from "@features/ecommerce/cart/cartQueryKeys";

/**
 * Clears all supplied lines through one BFF operation so prior-cart recommendation context can be
 * captured before the cart becomes empty. Invalidate-only, matching the existing cart mutation convention.
 */
const useClearCart = () => {
  const queryClient = useQueryClient();
  const api = useApi(EcommerceShoppingCartClient);

  return useMutation<void, Error, number[]>({
    mutationFn: async (lineNumbers) => {
      await api.clearShoppingCart(
        new ClearShoppingCartRequestDto({ lineNumbers }),
      );
    },
    onError: (error, lineNumbers) => {
      // Shared by single-item removal (useCart.removeItem passes one line) and full
      // clear, so scope the wording to how many lines the caller asked to delete.
      const action = lineNumbers.length === 1 ? "remove the item from" : "clear";
      Toast(`Failed to ${action} the cart. Reason: ${error?.message}`, true, 7000);
    },
    onSettled: async () => {
      await invalidateCart(queryClient);
    },
  });
};

export default useClearCart;
